import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { MIN_PASSWORD_LENGTH, type TermsDocument } from '@nexus/shared';

import { useSession } from '../../auth/SessionContext';
import { ApiError, apiGet, apiSend } from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  CheckboxField,
  FieldGroup,
  FieldRow,
  PageHeader,
  TextField,
} from '../../ui';

/**
 * Create an account.
 *
 * The terms are FETCHED and RENDERED here, and the hash of what was rendered
 * is posted back with the acceptance. That is not ceremony: the Worker refuses
 * an acceptance whose hash is not the hash of the document currently
 * published, so a creator can never be recorded as having agreed to words that
 * were changed while their tab was open. It also means this page cannot
 * summarise the terms — what it shows is what it hashes, which is why they are
 * on the page in full rather than behind a link.
 *
 * Whether an invitation code is REQUIRED is the API's call
 * (`/auth/signup-options`, from `SIGNUP_REQUIRES_INVITE`), not this page's:
 * when it is not, the field is still there — an issued code binds to the
 * address it was sent to and is honoured — but it moves to the end and says
 * "optional", because a required-looking field at the top of a form is where
 * a stranger without one gives up.
 */
export function Signup(): React.JSX.Element {
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('');
  const [inviteCode, setInviteCode] = useState(params.get('invite') ?? '');
  /**
   * `?ref=` into a VISIBLE, EDITABLE field, not a hidden input.
   *
   * A hidden field would mean a creator who followed a friend's link could
   * neither check that the right code came through nor remove one they did not
   * want, and the first they would hear of either is a support message weeks
   * later. It is also the only way somebody who was told a code out loud can
   * use it at all.
   */
  const [referralCode, setReferralCode] = useState(params.get('ref') ?? '');
  const [accepted, setAccepted] = useState(false);
  const [terms, setTerms] = useState<TermsDocument | null>(null);
  const [requiresInvite, setRequiresInvite] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<'password' | 'terms' | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { refresh } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    void (async () => {
      try {
        const body = await apiGet<{ terms: TermsDocument }>('/terms/creator');
        setTerms(body.terms);
      } catch {
        setError(
          'Could not load the creator terms, so signup is closed right now. Try again soon.',
        );
      }
    })();
    void (async () => {
      /* A failure here leaves the code field optional, which is the launch
         default; the API is what enforces the gate either way. */
      try {
        const body = await apiGet<{ requiresInvite: boolean }>('/auth/signup-options');
        setRequiresInvite(Boolean(body.requiresInvite));
      } catch {
        setRequiresInvite(false);
      }
    })();
  }, []);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!terms) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setInvalid('password');
      setError(`A password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (!accepted) {
      setInvalid('terms');
      setError('You have to accept the creator terms and the privacy policy to sign up.');
      return;
    }
    setBusy(true);
    setError(null);
    setInvalid(null);
    try {
      await apiSend('POST', '/auth/signup', {
        email,
        password,
        displayName: displayName.trim(),
        country: country.trim().toUpperCase(),
        acceptedTerms: { version: terms.version, documentHash: terms.documentHash },
        ...(inviteCode.trim() ? { inviteCode: inviteCode.trim() } : {}),
        ...(referralCode.trim() ? { referralCode: referralCode.trim() } : {}),
      });
      /**
       * STRAIGHT IN. Signup itself issues no session (routes/auth.ts keeps
       * that rule so the cookie never predates the account), so the form
       * signs in with the credentials it has just used and lands the
       * creator on Campaigns with the confirm-your-email banner across the
       * top — rather than on a page that asks a stranger to type the same
       * two things again. If the sign-in fails for any reason the old
       * "your account is ready, sign in" card is still the answer.
       */
      try {
        await apiSend('POST', '/auth/login', { email, password });
        await refresh();
        navigate('/campaigns');
        return;
      } catch {
        setDone(true);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  const inviteField = (
    <TextField
      label={requiresInvite ? 'Invitation code' : 'Invitation code (optional)'}
      type="text"
      name="inviteCode"
      value={inviteCode}
      onChange={(e) => setInviteCode(e.target.value)}
      placeholder="nxi-…"
      autoComplete="off"
      required={requiresInvite}
      hint={
        requiresInvite
          ? undefined
          : 'Only if the operator sent you one. It works for the address it was issued to.'
      }
    />
  );

  if (done) {
    return (
      <>
        <PageHeader
          eyebrow="Welcome"
          title="Your account is ready"
          lead="Sign in now. One link in your email finishes the setup."
        />
        <Card variant="feature">
          <p>
            We sent a confirmation link to <strong>{email}</strong>. You can sign in straight
            away and look around; confirming the address is what lets you submit clips and
            request payouts. The link lasts 24 hours and works once.
          </p>
          <Alert tone="waiting" title="Nothing arrived?">
            <span>
              Give it a minute and check your spam folder. After you sign in there is a Resend
              button at the top of every page.
            </span>
          </Alert>
          <div className="actions">
            <Link className="btn btn-primary" to="/login">
              Sign in
            </Link>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        hero
        eyebrow="Nexus"
        title="Create an account"
        lead="Cut clips from a campaign's source material, post them on your own accounts, and get paid per thousand verified views."
      />

      <Card variant="feature">
        <form onSubmit={submit} className="form">
          {requiresInvite && (
            <FieldGroup
              legend="Your invitation"
              note="Nexus needs an invitation code right now. The code came with your invitation and only works for the address it was sent to."
            >
              {inviteField}
            </FieldGroup>
          )}

          <FieldGroup legend="About you">
            <TextField
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hint="We send one confirmation link here. No newsletters."
            />
            <FieldRow>
              <TextField
                label="Name"
                type="text"
                name="displayName"
                autoComplete="nickname"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <TextField
                label="Country"
                type="text"
                name="country"
                autoComplete="country"
                required
                maxLength={2}
                value={country}
                onChange={(e) => setCountry(e.target.value.toUpperCase())}
                placeholder="US"
                hint="Two letters: US, GB, DE. It decides how you can be paid."
              />
            </FieldRow>
            <TextField
              label="Password"
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
              {...(invalid === 'password' ? { 'aria-invalid': true } : {})}
            />
          </FieldGroup>

          {/* Separate from the invitation, because they are different things and
              both may be present: the invitation is the gate, the referral is
              who gets paid. Neither replaces the other. */}
          <FieldGroup
            legend="Were you referred?"
            note="If a friend sent you here, their code earns them 10% of what you earn — it costs you nothing and changes nothing about how you are paid."
          >
            <TextField
              label="Referral code (optional)"
              type="text"
              name="referralCode"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="nx-…"
              autoComplete="off"
              hint="It can only be given now — a code cannot be added to an account later."
            />
            {!requiresInvite && inviteField}
          </FieldGroup>

          <FieldGroup
            legend="The creator terms"
            note={
              terms
                ? `Version ${terms.version}. This is the exact text your acceptance is recorded against — not a summary of it.`
                : 'Loading the current version…'
            }
          >
            {terms && <pre className="prose-block">{terms.bodyText}</pre>}
            <CheckboxField
              name="acceptTerms"
              checked={accepted}
              onChange={(e) => {
                setAccepted(e.target.checked);
                if (e.target.checked && invalid === 'terms') setInvalid(null);
              }}
              {...(invalid === 'terms' ? { 'aria-invalid': true } : {})}
              label={
                <>
                  I accept the creator terms{terms ? ` (${terms.version})` : ''} and the{' '}
                  <Link to="/privacy" target="_blank" rel="noreferrer">
                    privacy policy
                  </Link>
                </>
              }
            />
          </FieldGroup>

          {error && <Alert tone="critical">{error}</Alert>}

          <Button type="submit" variant="primary" disabled={busy || terms === null}>
            {busy ? 'Creating…' : 'Create account'}
          </Button>
        </form>
      </Card>

      <Card tight>
        <p className="muted">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </Card>
    </>
  );
}
